<template>
  <div class="bg-background text-foreground font-sans">

    <!-- Navigation -->
    <nav class="sticky top-0 z-50 bg-background transition-all" :class="scrolled ? 'border-b border-border shadow-sm' : ''">
      <div class="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <router-link to="/"><img src="/logo/applypa-logo.png" alt="Applypa" class="h-8 w-auto" /></router-link>

        <div class="hidden md:flex items-center gap-8">
          <a href="#how" class="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
          <a href="#pricing" class="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
          <router-link to="/register" class="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">For Freelancers</router-link>
        </div>

        <a href="https://calendly.com/jobzing/15min" target="_blank" rel="noopener" class="hidden md:inline-flex px-5 py-2.5 bg-primary hover:bg-primary-dark text-primary-foreground text-sm font-semibold rounded-lg transition-colors">
          Book a Call
        </a>

        <button class="md:hidden p-2" aria-label="Menu" @click="mobileMenuOpen = !mobileMenuOpen">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Mobile menu -->
      <div v-if="mobileMenuOpen" class="md:hidden border-t border-border bg-background px-6 py-4 space-y-3">
        <a href="#how" class="block text-sm font-medium text-muted-foreground hover:text-foreground" @click="mobileMenuOpen = false">How It Works</a>
        <a href="#pricing" class="block text-sm font-medium text-muted-foreground hover:text-foreground" @click="mobileMenuOpen = false">Pricing</a>
        <router-link to="/register" class="block text-sm font-medium text-muted-foreground hover:text-foreground" @click="mobileMenuOpen = false">For Freelancers</router-link>
        <a href="https://calendly.com/jobzing/15min" target="_blank" rel="noopener" class="block w-full text-center px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg transition-colors">
          Book a Call
        </a>
      </div>
    </nav>

    <!-- Hero -->
    <section class="relative overflow-hidden">
      <div class="max-w-6xl mx-auto px-6 pt-8 pb-32 md:pt-12 md:pb-48">
        <div class="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">

          <!-- Left: Text -->
          <div class="lg:col-span-3">
            <div class="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/15 border border-amber-500/30 rounded-full mb-6">
              <span class="w-2 h-2 bg-amber-500 rounded-full"></span>
              <span class="text-sm font-medium text-amber-700 dark:text-amber-300">Vetted Filipino talent marketplace</span>
            </div>
            <h1 class="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-foreground leading-none">
              Hire a pre-interviewed Filipino developer.
              <span class="text-muted-foreground"> Skip the 200-applicant pile.</span>
            </h1>
            <p class="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
              We personally interview every Filipino developer on Applypa. You get 3 shortlisted candidates in 7 days — all ready to start, all backed by our 30-day replacement guarantee.
            </p>
            <div class="mt-10 flex flex-col sm:flex-row gap-4">
              <a href="https://calendly.com/jobzing/15min" target="_blank" rel="noopener" class="inline-flex items-center justify-center px-8 py-4 bg-primary hover:bg-primary-dark text-primary-foreground font-semibold rounded-lg transition-colors text-base">
                Book a 15-min call
              </a>
              <a href="#how" class="inline-flex items-center justify-center px-8 py-4 text-primary hover:text-primary-dark font-semibold transition-colors text-base">
                See how it works →
              </a>
            </div>
            <div class="mt-12 flex flex-wrap gap-6 text-sm text-muted-foreground">
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                Pre-vetted developers
              </div>
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                30-day replacement guarantee
              </div>
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                Starts within 7 days
              </div>
            </div>
          </div>

          <!-- Right: Candidate Card Preview -->
          <div class="lg:col-span-2">
            <div class="relative">
              <div class="absolute -inset-4 bg-gradient-to-br from-amber-500/20 to-blue-500/20 rounded-3xl blur-2xl opacity-60"></div>
              <div class="relative bg-card border border-border rounded-2xl p-6 shadow-xl">
                <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-4">Sample shortlist card</p>
                <div class="flex items-start gap-4">
                  <div class="w-16 h-16 bg-gradient-to-br from-blue-500/40 to-blue-600/40 rounded-full flex-shrink-0"></div>
                  <div class="flex-1">
                    <div class="flex items-center gap-2">
                      <h3 class="text-lg font-bold text-foreground">Jose M.</h3>
                      <span class="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/15 border border-green-500/30 rounded-full text-xs font-medium text-green-700 dark:text-green-300">
                        ✓ Verified
                      </span>
                    </div>
                    <p class="text-sm text-muted-foreground mt-0.5">Senior Full-Stack Developer · 6+ years</p>
                  </div>
                </div>
                <div class="mt-5 flex flex-wrap gap-2">
                  <span class="px-2.5 py-1 bg-muted text-xs font-medium text-muted-foreground rounded-md">React</span>
                  <span class="px-2.5 py-1 bg-muted text-xs font-medium text-muted-foreground rounded-md">Node.js</span>
                  <span class="px-2.5 py-1 bg-muted text-xs font-medium text-muted-foreground rounded-md">PostgreSQL</span>
                </div>
                <div class="mt-5 pt-5 border-t border-border flex items-center justify-between">
                  <div>
                    <p class="text-2xl font-bold text-foreground">$15<span class="text-sm font-medium text-muted-foreground">/hr</span></p>
                    <p class="text-xs text-muted-foreground mt-0.5">Previously hired by 2 employers</p>
                  </div>
                  <a href="https://calendly.com/jobzing/15min" target="_blank" rel="noopener" class="px-4 py-2 bg-primary hover:bg-primary-dark text-primary-foreground text-sm font-semibold rounded-lg transition-colors">
                    Interview →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Problem Section -->
    <section class="bg-muted py-20 md:py-32">
      <div class="max-w-3xl mx-auto px-6">
        <h2 class="text-3xl md:text-5xl font-bold tracking-tight text-foreground text-center leading-tight">
          Hiring a Filipino developer shouldn't feel like a gamble
        </h2>
        <div class="mt-12 space-y-6 text-lg text-muted-foreground leading-relaxed">
          <p>
            You post a job on a freelance marketplace or job board. Within 24 hours, you have 200 applications. Most are copy-paste. A few look promising. You schedule 5 interviews — 3 ghost you, 1 was misrepresenting their skills, and the 5th is decent but you're not sure.
          </p>
          <p>
            Two weeks pass. You still don't have a hire. You've spent 10+ hours sifting, interviewing, and double-checking GitHub portfolios. And you're no closer to filling the role than when you started.
          </p>
          <p class="text-xl font-semibold text-foreground">
            We built Applypa because we watched this happen over and over — to smart founders who just wanted to hire someone good, fast.
          </p>
        </div>
      </div>
    </section>

    <!-- How It Works -->
    <section id="how" class="py-20 md:py-32">
      <div class="max-w-6xl mx-auto px-6">
        <div class="text-center max-w-2xl mx-auto">
          <h2 class="text-3xl md:text-5xl font-bold tracking-tight text-foreground">How Applypa works</h2>
          <p class="mt-4 text-lg md:text-xl text-muted-foreground">From kickoff call to hired in under 14 days</p>
        </div>

        <div class="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div class="relative">
            <div class="flex items-center justify-center w-12 h-12 bg-primary text-primary-foreground font-bold rounded-full">1</div>
            <h3 class="mt-6 text-xl font-bold text-foreground">15-minute kickoff call</h3>
            <p class="mt-3 text-muted-foreground leading-relaxed">
              You tell us what you need — stack, seniority, budget, timezone. We ask sharp questions to make sure we get it right.
            </p>
          </div>
          <div class="relative">
            <div class="flex items-center justify-center w-12 h-12 bg-primary text-primary-foreground font-bold rounded-full">2</div>
            <h3 class="mt-6 text-xl font-bold text-foreground">We hand-pick 3 candidates</h3>
            <p class="mt-3 text-muted-foreground leading-relaxed">
              Within 5-7 days, we send you a shortlist of 3 pre-interviewed Filipino developers. Each comes with interview notes, GitHub portfolio review, and why they fit your specific needs.
            </p>
          </div>
          <div class="relative">
            <div class="flex items-center justify-center w-12 h-12 bg-primary text-primary-foreground font-bold rounded-full">3</div>
            <h3 class="mt-6 text-xl font-bold text-foreground">You interview your top picks</h3>
            <p class="mt-3 text-muted-foreground leading-relaxed">
              We introduce you directly. No middlemen. You interview them like any other hire. Give them a small task if you want. Take your time.
            </p>
          </div>
          <div class="relative">
            <div class="flex items-center justify-center w-12 h-12 bg-primary text-primary-foreground font-bold rounded-full">4</div>
            <h3 class="mt-6 text-xl font-bold text-foreground">Hire with confidence</h3>
            <p class="mt-3 text-muted-foreground leading-relaxed">
              You hire directly. They work for you, not us. If it doesn't work out in 30 days, we replace them at no cost.
            </p>
          </div>
        </div>

        <div class="mt-16 text-center">
          <a href="https://calendly.com/jobzing/15min" target="_blank" rel="noopener" class="inline-flex items-center justify-center px-8 py-4 bg-primary hover:bg-primary-dark text-primary-foreground font-semibold rounded-lg transition-colors">
            Book your kickoff call →
          </a>
        </div>
      </div>
    </section>

    <!-- Why We're Different -->
    <section class="bg-muted py-20 md:py-32">
      <div class="max-w-6xl mx-auto px-6">
        <div class="text-center max-w-2xl mx-auto">
          <h2 class="text-3xl md:text-5xl font-bold tracking-tight text-foreground">We didn't build another job board</h2>
          <p class="mt-4 text-lg md:text-xl text-muted-foreground">We built the thing we wished existed when we were hiring Filipino talent ourselves.</p>
        </div>

        <!-- Desktop table -->
        <div class="mt-16 hidden md:block">
          <div class="bg-card border border-border rounded-2xl overflow-hidden">
            <table class="w-full">
              <thead class="bg-muted">
                <tr class="border-b border-border">
                  <th class="text-left px-6 py-4 text-sm font-semibold text-foreground w-1/4"></th>
                  <th class="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Freelance Marketplaces</th>
                  <th class="text-left px-6 py-4 text-sm font-semibold text-muted-foreground">Staffing Agencies</th>
                  <th class="text-left px-6 py-4 text-sm font-semibold text-amber-600 dark:text-amber-400 bg-amber-500/10">Applypa</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-border">
                <tr>
                  <td class="px-6 py-5 font-medium text-foreground">How vetting works</td>
                  <td class="px-6 py-5 text-muted-foreground">AI scoring, self-reported</td>
                  <td class="px-6 py-5 text-muted-foreground">Hidden behind agency wall</td>
                  <td class="px-6 py-5 text-foreground bg-amber-500/10 font-medium">Personal interviews with every candidate</td>
                </tr>
                <tr>
                  <td class="px-6 py-5 font-medium text-foreground">Time to hire</td>
                  <td class="px-6 py-5 text-muted-foreground">2-4 weeks</td>
                  <td class="px-6 py-5 text-muted-foreground">2-6 weeks</td>
                  <td class="px-6 py-5 text-foreground bg-amber-500/10 font-medium">7-14 days</td>
                </tr>
                <tr>
                  <td class="px-6 py-5 font-medium text-foreground">Markup on worker pay</td>
                  <td class="px-6 py-5 text-muted-foreground">None (but you do all the work)</td>
                  <td class="px-6 py-5 text-muted-foreground">50-200% hidden markup</td>
                  <td class="px-6 py-5 text-foreground bg-amber-500/10 font-medium">Flat fee, no markup</td>
                </tr>
                <tr>
                  <td class="px-6 py-5 font-medium text-foreground">Who worker reports to</td>
                  <td class="px-6 py-5 text-muted-foreground">You</td>
                  <td class="px-6 py-5 text-muted-foreground">The agency</td>
                  <td class="px-6 py-5 text-foreground bg-amber-500/10 font-medium">You directly</td>
                </tr>
                <tr>
                  <td class="px-6 py-5 font-medium text-foreground">If hire doesn't work out</td>
                  <td class="px-6 py-5 text-muted-foreground">You start over</td>
                  <td class="px-6 py-5 text-muted-foreground">Maybe a replacement</td>
                  <td class="px-6 py-5 text-foreground bg-amber-500/10 font-medium">30-day guaranteed replacement</td>
                </tr>
                <tr>
                  <td class="px-6 py-5 font-medium text-foreground">What you get</td>
                  <td class="px-6 py-5 text-muted-foreground">200 applications</td>
                  <td class="px-6 py-5 text-muted-foreground">1-2 opaque candidates</td>
                  <td class="px-6 py-5 text-foreground bg-amber-500/10 font-medium">3 hand-picked with notes</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Mobile: Jobzing advantages only -->
        <div class="mt-16 md:hidden">
          <div class="bg-card border-2 border-amber-500/30 rounded-2xl p-6">
            <h3 class="text-xl font-bold text-amber-600 dark:text-amber-400 mb-4">Why Applypa</h3>
            <ul class="space-y-3 text-muted-foreground">
              <li class="flex gap-3"><span class="text-green-500 flex-shrink-0">✓</span><span>Personal interviews with every candidate</span></li>
              <li class="flex gap-3"><span class="text-green-500 flex-shrink-0">✓</span><span>7-14 days to hire</span></li>
              <li class="flex gap-3"><span class="text-green-500 flex-shrink-0">✓</span><span>Flat fee, no markup on worker pay</span></li>
              <li class="flex gap-3"><span class="text-green-500 flex-shrink-0">✓</span><span>Developer reports to you directly</span></li>
              <li class="flex gap-3"><span class="text-green-500 flex-shrink-0">✓</span><span>30-day guaranteed replacement</span></li>
              <li class="flex gap-3"><span class="text-green-500 flex-shrink-0">✓</span><span>3 hand-picked candidates with our notes</span></li>
            </ul>
          </div>
        </div>

        <p class="mt-12 text-center text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          We handle the sorting, the interviewing, and the gut-checks. You handle what you do best — running your business.
        </p>
      </div>
    </section>

    <!-- Social Proof -->
    <section class="py-20 md:py-32">
      <div class="max-w-6xl mx-auto px-6">
        <h2 class="text-3xl md:text-5xl font-bold tracking-tight text-foreground text-center">
          Founders hiring with Applypa
        </h2>

        <div class="mt-16 grid md:grid-cols-3 gap-6">
          <div class="bg-muted border border-dashed border-border rounded-2xl p-8 flex flex-col items-center justify-center text-center">
            <p class="text-muted-foreground mb-2">First 3 clients get featured here.</p>
            <p class="font-semibold text-foreground mb-4">Want to be one?</p>
            <a href="https://calendly.com/jobzing/15min" target="_blank" rel="noopener" class="text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-semibold text-sm">Book a call →</a>
          </div>
          <div class="bg-muted border border-dashed border-border rounded-2xl p-8 flex flex-col items-center justify-center text-center">
            <p class="text-muted-foreground mb-2">Spot #2 still open.</p>
            <p class="font-semibold text-foreground mb-4">Hire smarter today.</p>
            <a href="https://calendly.com/jobzing/15min" target="_blank" rel="noopener" class="text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-semibold text-sm">Book a call →</a>
          </div>
          <div class="bg-muted border border-dashed border-border rounded-2xl p-8 flex flex-col items-center justify-center text-center">
            <p class="text-muted-foreground mb-2">Spot #3 still open.</p>
            <p class="font-semibold text-foreground mb-4">Ready to hire smart?</p>
            <a href="https://calendly.com/jobzing/15min" target="_blank" rel="noopener" class="text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-semibold text-sm">Book a call →</a>
          </div>
        </div>

        <!-- Stats row -->
        <div class="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 pt-12 border-t border-border">
          <div class="text-center">
            <p class="text-4xl md:text-5xl font-bold text-foreground">50+</p>
            <p class="mt-2 text-muted-foreground">Filipino developers interviewed</p>
          </div>
          <div class="text-center">
            <p class="text-4xl md:text-5xl font-bold text-foreground">7</p>
            <p class="mt-2 text-muted-foreground">Avg days to shortlist</p>
          </div>
          <div class="text-center">
            <p class="text-4xl md:text-5xl font-bold text-foreground">100%</p>
            <p class="mt-2 text-muted-foreground">Replacement guarantee</p>
          </div>
        </div>
      </div>
    </section>

    <div class="max-w-6xl mx-auto px-6">
      <div class="h-px bg-muted"></div>
    </div>

    <!-- Proof -->
    <section class="py-20 md:py-32">
      <div class="max-w-6xl mx-auto px-6">
        <h2 class="text-3xl md:text-5xl font-bold tracking-tight text-foreground text-center">
          Why clients hire developers with Arc
        </h2>
        <p class="mt-4 text-lg text-muted-foreground text-center max-w-2xl mx-auto">
          Real founder feedback from recent placements.
        </p>

        <div class="mt-16 grid md:grid-cols-3 gap-10">
          <div>
            <div class="text-5xl font-black text-foreground leading-none">“</div>
            <p class="mt-4 text-muted-foreground leading-relaxed">
              Without Applypa, I’d still be wasting days sorting applicants and second-guessing signals. The shortlist was small, clear, and actually vetted.
            </p>

            <div class="mt-8 flex items-center gap-4">
              <div class="w-1 h-8 bg-primary rounded-full"></div>
              <div class="w-11 h-11 rounded-full bg-muted border border-border"></div>
              <div>
                <p class="font-semibold text-foreground">Mitchum Owen</p>
                <p class="text-sm text-muted-foreground">Founder</p>
              </div>
            </div>
          </div>

          <div>
            <div class="text-5xl font-black text-foreground leading-none">“</div>
            <p class="mt-4 text-muted-foreground leading-relaxed">
              We filled the role faster than expected and the candidate quality was strong. The interview notes made it easy to decide quickly.
            </p>

            <div class="mt-8 flex items-center gap-4">
              <div class="w-1 h-8 bg-primary rounded-full"></div>
              <div class="w-11 h-11 rounded-full bg-muted border border-border"></div>
              <div>
                <p class="font-semibold text-foreground">Matt Gysel</p>
                <p class="text-sm text-muted-foreground">Operator</p>
              </div>
            </div>
          </div>

          <div>
            <div class="text-5xl font-black text-foreground leading-none">“</div>
            <p class="mt-4 text-muted-foreground leading-relaxed">
              The biggest win is the time saved sourcing and vetting. Instead of 100+ applications, we reviewed 3 and hired within a week.
            </p>

            <div class="mt-8 flex items-center gap-4">
              <div class="w-1 h-8 bg-primary rounded-full"></div>
              <div class="w-11 h-11 rounded-full bg-muted border border-border"></div>
              <div>
                <p class="font-semibold text-foreground">Philip Tsai</p>
                <p class="text-sm text-muted-foreground">Director</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing -->
    <section id="pricing" class="bg-muted py-20 md:py-32">
      <div class="max-w-6xl mx-auto px-6">
        <div class="text-center max-w-2xl mx-auto">
          <h2 class="text-3xl md:text-5xl font-bold tracking-tight text-foreground">Simple, transparent pricing</h2>
          <p class="mt-4 text-lg md:text-xl text-muted-foreground">Pay only when you hire. No long-term commitment.</p>
        </div>

        <div class="mt-16 max-w-lg mx-auto">
          <div class="relative bg-card border-2 border-primary rounded-2xl p-8 md:p-10 shadow-xl">
            <div class="absolute -top-4 left-1/2 -translate-x-1/2">
              <span class="inline-block px-4 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full uppercase tracking-wide">Most popular</span>
            </div>

            <div class="text-center">
              <p class="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Placement Package</p>
              <div class="flex items-baseline justify-center gap-2">
                <span class="text-6xl font-bold text-foreground">$299</span>
                <span class="text-lg text-muted-foreground">USD</span>
              </div>
              <p class="mt-2 text-muted-foreground">per successful placement</p>
              <p class="mt-1 text-sm text-muted-foreground">≈ ₱17,500 PHP</p>
            </div>

            <ul class="mt-8 space-y-4">
              <li v-for="feature in pricingFeatures" :key="feature" class="flex gap-3">
                <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                <span class="text-muted-foreground">{{ feature }}</span>
              </li>
            </ul>

            <a href="https://calendly.com/jobzing/15min" target="_blank" rel="noopener" class="mt-8 block w-full px-6 py-4 bg-primary hover:bg-primary-dark text-primary-foreground font-semibold text-center rounded-lg transition-colors">
              Get started →
            </a>

            <p class="mt-6 text-sm text-muted-foreground text-center leading-relaxed">
              Payment collected only after you receive your shortlist. If none of our candidates fit, full refund.
            </p>
          </div>

          <!-- Just exploring? -->
          <div class="mt-8 bg-card/50 border border-border rounded-xl p-6 text-center">
            <p class="font-semibold text-foreground mb-1">Just exploring?</p>
            <p class="text-muted-foreground text-sm mb-4">Book a free 15-minute call. We'll answer questions about hiring Filipino developers — no sales pressure, no commitment.</p>
            <a href="https://calendly.com/jobzing/15min" target="_blank" rel="noopener" class="inline-block text-primary hover:text-primary-dark font-semibold text-sm">
              Book free call →
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Filipino Talent -->
    <section class="py-20 md:py-32">
      <div class="max-w-6xl mx-auto px-6">
        <div class="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 class="text-3xl md:text-5xl font-bold tracking-tight text-foreground leading-tight">
              Why Filipino developers?
            </h2>
            <p class="mt-6 text-lg text-muted-foreground leading-relaxed">
              The Philippines has been quietly producing some of the world's best remote workers for 20+ years. Here's why our clients keep coming back:
            </p>
            <div class="mt-8 space-y-6">
              <div>
                <h3 class="font-bold text-foreground text-lg mb-2">Native-level English</h3>
                <p class="text-muted-foreground leading-relaxed">
                  Filipino professionals learn English from childhood — not just in school, but in movies, music, and daily life. Code reviews and standups are never a communication barrier.
                </p>
              </div>
              <div>
                <h3 class="font-bold text-foreground text-lg mb-2">Genuine work ethic</h3>
                <p class="text-muted-foreground leading-relaxed">
                  Filipino culture values loyalty and long-term relationships. Your developers don't job-hop every 3 months. They build with you.
                </p>
              </div>
              <div>
                <h3 class="font-bold text-foreground text-lg mb-2">Specialized skills at honest rates</h3>
                <p class="text-muted-foreground leading-relaxed">
                  A senior Filipino developer earns $12-25/hour — life-changing income in the Philippines, honest value for you. Nobody loses.
                </p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
            <div class="bg-gradient-to-br from-primary-dark to-primary text-white rounded-2xl p-8">
              <p class="text-5xl font-bold">$15<span class="text-2xl">/hr</span></p>
              <p class="mt-2 text-muted-foreground">Average developer rate</p>
            </div>
            <div class="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-2xl p-8">
              <p class="text-5xl font-bold">4yr</p>
              <p class="mt-2 text-amber-100">Avg tenure with one employer</p>
            </div>
            <div class="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-2xl p-8">
              <p class="text-5xl font-bold">97%</p>
              <p class="mt-2 text-blue-100">English literacy rate</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section id="faq" class="bg-muted py-20 md:py-32">
      <div class="max-w-3xl mx-auto px-6">
        <h2 class="text-3xl md:text-5xl font-bold tracking-tight text-foreground text-center">
          Frequently asked questions
        </h2>

        <div class="mt-16 space-y-4">
          <details v-for="faq in faqs" :key="faq.q" class="group bg-card border border-border rounded-xl overflow-hidden">
            <summary class="flex items-center justify-between px-6 py-5 cursor-pointer list-none">
              <span class="font-semibold text-foreground pr-4">{{ faq.q }}</span>
              <svg class="w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div class="px-6 pb-5 text-muted-foreground leading-relaxed">{{ faq.a }}</div>
          </details>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="bg-primary py-20 md:py-32">
      <div class="max-w-3xl mx-auto px-6 text-center">
        <h2 class="text-3xl md:text-5xl font-bold tracking-tight text-white leading-tight">
          Get 3 vetted developers in 7 days
        </h2>
        <p class="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed">
          Book a free 15-minute call. We'll learn what you need and tell you honestly if we can help.
        </p>
        <div class="mt-10">
          <a href="https://calendly.com/jobzing/15min" target="_blank" rel="noopener" class="inline-flex items-center justify-center px-10 py-5 bg-card hover:bg-accent text-primary font-semibold text-lg rounded-lg transition-colors">
            Book your free call →
          </a>
        </div>
        <p class="mt-6 text-sm text-muted-foreground">No sales pitch. No commitment. Just a straight conversation.</p>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-gray-950 text-muted-foreground py-16">
      <div class="max-w-6xl mx-auto px-6">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div class="col-span-2 md:col-span-1">
            <p class="text-2xl font-bold text-white">Applypa</p>
            <p class="mt-3 text-sm leading-relaxed">Pre-interviewed Filipino talent, served fresh.</p>
            <div class="mt-4 flex gap-3">
              <a href="#" class="text-muted-foreground hover:text-white transition-colors" aria-label="Twitter">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              </a>
              <a href="#" class="text-muted-foreground hover:text-white transition-colors" aria-label="LinkedIn">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
              </a>
            </div>
          </div>

          <div>
            <p class="font-semibold text-white mb-4">Company</p>
            <ul class="space-y-2 text-sm">
              <li><a href="#how" class="hover:text-white transition-colors">How It Works</a></li>
              <li><a href="#pricing" class="hover:text-white transition-colors">Pricing</a></li>
              <li><a href="#faq" class="hover:text-white transition-colors">FAQ</a></li>
            </ul>
          </div>

          <div>
            <p class="font-semibold text-white mb-4">For Workers</p>
            <ul class="space-y-2 text-sm">
              <li><router-link to="/register" class="hover:text-white transition-colors">Apply to Applypa Verified</router-link></li>
              <li><router-link to="/register" class="hover:text-white transition-colors">For Filipino Developers</router-link></li>
            </ul>
          </div>

          <div>
            <p class="font-semibold text-white mb-4">Legal</p>
            <ul class="space-y-2 text-sm">
              <li><a href="/privacy" class="hover:text-white transition-colors">Privacy Policy</a></li>
              <li><a href="/terms" class="hover:text-white transition-colors">Terms of Service</a></li>
              <li><a href="mailto:hello@jobzing.com" class="hover:text-white transition-colors">hello@jobzing.com</a></li>
            </ul>
          </div>
        </div>

        <div class="mt-12 pt-8 border-t border-gray-800 text-sm text-muted-foreground text-center md:text-left">
          © 2026 Elevate Solutions Experts. Applypa is a product of Elevate Solutions Experts. · Philippines
        </div>
      </div>
    </footer>

  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';

export default {
  name: 'DevelopersLandingPage',
  setup() {
    const mobileMenuOpen = ref(false);
    const scrolled = ref(false);

    const handleScroll = () => {
      scrolled.value = window.scrollY > 10;
    };

    onMounted(() => window.addEventListener('scroll', handleScroll));
    onUnmounted(() => window.removeEventListener('scroll', handleScroll));

    const pricingFeatures = [
      '15-minute kickoff call',
      '3 pre-interviewed candidates in 5-7 days',
      'Full candidate dossier (skills test, interview notes, GitHub review)',
      'Direct introductions — no middlemen',
      '30-day replacement guarantee',
      'Email support throughout hiring',
    ];

    const faqs = [
      {
        q: 'How is this different from a freelance job board?',
        a: 'A typical freelance job board gives you access to millions of profiles and lets its AI score applicants to your job post. You still review the shortlist, run the interviews, and handle the hiring process yourself. Applypa does the interviewing for you. We personally talk to every developer before recommending them. You get 3 ready-to-hire people instead of 200 applications to review.',
      },
      {
        q: 'Who owns the employment relationship?',
        a: "You do, 100%. The Filipino developer works for you directly. We make the introduction and help you find the right fit. We don't take any cut of their salary, and they don't report to us. You hire them on your terms.",
      },
      {
        q: "What if the person I hire doesn't work out?",
        a: "Within the first 30 days, we replace them at no cost. We'll send you 2-3 new candidates and help you get the right person in place. This is why we vet so carefully upfront — we don't want to do this twice.",
      },
      {
        q: 'How much do Filipino developers actually cost?',
        a: 'Rates depend on experience and stack. For developers, expect $8-25/hour for quality talent. Our placement fee ($299) is paid once, upfront. After that, you pay them directly — no hidden markup, no ongoing fees to us.',
      },
      {
        q: 'How do you actually vet developers?',
        a: 'Three steps: (1) a technical skills assessment specific to their stack, (2) a 15-minute live interview with our team covering problem-solving and communication, (3) a GitHub/portfolio review and reference check with at least one past employer. Only about 1 in 5 applicants passes all three.',
      },
      {
        q: 'How long does the whole process take?',
        a: 'Typically 7-14 days from kickoff call to hired. We send your shortlist within 5-7 days. Most clients interview all 3 candidates within a week and make a decision shortly after.',
      },
      {
        q: 'What developer roles do you place?',
        a: "Right now we handle full-stack, frontend, backend, and mobile developers. We're focused on depth over breadth — being the best place to hire Filipino developers rather than an average place to hire everyone. Email us if you need a specific stack and we'll tell you if we can help.",
      },
      {
        q: 'How does payment work?',
        a: 'We accept payments in USD, EUR, and GBP via Wise, bank wire, or credit card. Payment is collected after you receive your shortlist — if none of our candidates fit, you get a full refund.',
      },
    ];

    return { mobileMenuOpen, scrolled, pricingFeatures, faqs };
  },
};
</script>
